import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { StatusPill, TrustChips, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Pencil, Send, Loader2, AlertCircle, MessageSquarePlus, ArrowLeft, Users, UserPlus, Trophy, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const STATUS_HELP = {
  draft: "Your campaign is a draft. Finish it and submit for review.",
  submitted: "Submitted! Our team is reviewing your campaign before it can accept public donations.",
  under_review: "Your campaign is under review.",
  needs_info: "We need more information. Review the notes below, update your campaign, and resubmit.",
  live: "Your campaign is live and accepting donations.",
  approved: "Approved and live.",
  rejected: "This campaign was not approved. See the notes below.",
  paused: "This campaign is paused.",
  completed: "This campaign is completed.",
};

export default function CampaignStatus() {
  const { campaignId } = useParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const [update, setUpdate] = useState({ title: "", body: "" });
  const [posting, setPosting] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: "", personal_goal: "", message: "" });
  const [addingMember, setAddingMember] = useState(false);

  const load = () => {
    api.get("/campaigns/mine").then((r) => {
      const found = r.data.find((x) => x.id === campaignId);
      if (found) { setC(found); loadDetail(found.slug); }
      else setError("Campaign not found");
    }).catch((e) => setError(errMsg(e)));
  };
  const loadDetail = (slug) => api.get(`/campaigns/${slug}`).then((r) => setC(r.data)).catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignId]);

  const postUpdate = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      await api.post(`/campaigns/${campaignId}/updates`, update);
      setUpdate({ title: "", body: "" });
      toast.success("Update posted.");
      loadDetail(c.slug);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setPosting(false); }
  };

  const addMember = async (e) => {
    e.preventDefault();
    setAddingMember(true);
    try {
      await api.post(`/campaigns/${campaignId}/team-members`, {
        name: teamForm.name,
        personal_goal: teamForm.personal_goal ? Number(teamForm.personal_goal) : null,
        message: teamForm.message,
      });
      setTeamForm({ name: "", personal_goal: "", message: "" });
      toast.success("Team member added.");
      loadDetail(c.slug);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setAddingMember(false); }
  };

  const removeMember = async (mid) => {
    try {
      await api.delete(`/campaigns/${campaignId}/team-members/${mid}`);
      toast.success("Member removed.");
      loadDetail(c.slug);
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (error) return <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>;
  if (!c) return <InlineLoader label="Loading" />;

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/organizer/campaigns" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-ff-medium hover:text-ff-red">
        <ArrowLeft className="h-4 w-4" /> All campaigns
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">{c.title}</h1>
          <div className="mt-2"><StatusPill status={c.status} /></div>
        </div>
        <Button asChild variant="outline" className="border-ff-blue text-ff-blue hover:bg-ff-blue hover:text-white">
          <Link to={`/organizer/campaign-builder/${c.id}`} data-testid="status-edit"><Pencil className="mr-2 h-4 w-4" /> Edit</Link>
        </Button>
      </div>

      <div className="mt-5 rounded-xl border border-ff-border bg-[#F3F4F6] p-5 text-sm text-ff-dark" data-testid="status-help">
        {STATUS_HELP[c.status] || "Status updated."}
      </div>

      {c.status === "under_review" && ((c.review_reasons?.length > 0) || c.review_notes) && (
        <div className="mt-5 rounded-xl border border-ff-amber/40 bg-[#FFF7ED] p-5" data-testid="under-review-detail">
          <div className="flex items-center gap-2 font-heading font-bold text-ff-blue"><ShieldAlert className="h-5 w-5 text-ff-amber" /> Your campaign is under review</div>
          <p className="mt-1 text-sm text-ff-dark">Donations are paused while our Trust &amp; Safety team takes a closer look. Here's what we're reviewing:</p>
          {c.review_reasons?.length > 0 && (
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-ff-dark" data-testid="under-review-reasons">
              {c.review_reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
          {c.review_notes && (
            <div className="mt-3 rounded-lg bg-white/70 p-3 text-sm text-ff-dark" data-testid="under-review-notes"><b>Note from our team:</b> {c.review_notes}</div>
          )}
          <div className="mt-3 text-xs text-ff-medium">What you can do: update your campaign details above, then reply to our email with any documents or clarification. We'll notify you once the review is complete.</div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-3 gap-4">
        <Metric label="Raised" value={money(c.amount_raised)} />
        <Metric label="Donors" value={c.donor_count || 0} />
        <Metric label="Goal" value={c.goal_amount ? money(c.goal_amount) : "—"} />
      </div>

      {c.trust_labels?.length > 0 && <div className="mt-5"><TrustChips labels={c.trust_labels} /></div>}

      {/* Post an update */}
      <div className="mt-8 rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
        <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-ff-blue"><MessageSquarePlus className="h-5 w-5" /> Post an update</h3>
        <form onSubmit={postUpdate} className="mt-4 space-y-3" data-testid="update-form">
          <div><Label>Title</Label><Input required data-testid="update-title" value={update.title} onChange={(e) => setUpdate({ ...update, title: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Message</Label><Textarea required rows={3} data-testid="update-body" value={update.body} onChange={(e) => setUpdate({ ...update, body: e.target.value })} className="mt-1.5" /></div>
          <Button type="submit" disabled={posting} data-testid="update-submit" className="bg-ff-blue text-white hover:bg-ff-blue/90">
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Post update</>}
          </Button>
        </form>
        {c.updates?.length > 0 && (
          <div className="mt-6 space-y-3 border-t border-ff-border pt-4">
            {c.updates.map((u) => (
              <div key={u.id} className="rounded-lg bg-[#F3F4F6] p-3">
                <div className="text-xs text-ff-medium">{new Date(u.created_at).toLocaleDateString()}</div>
                <div className="font-heading font-bold text-ff-blue">{u.title}</div>
                <div className="text-sm text-ff-dark">{u.body}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {c.is_team && (
        <div className="mt-8 rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid="team-management">
          <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-ff-blue"><Users className="h-5 w-5" /> Team members</h3>
          <p className="mt-1 text-sm text-ff-medium">Add sub-fundraisers. Donors can support the whole team or an individual member.</p>
          <form onSubmit={addMember} className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="add-member-form">
            <Input required data-testid="member-name" placeholder="Member name" value={teamForm.name} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} />
            <Input type="number" data-testid="member-goal" placeholder="Personal goal ($)" value={teamForm.personal_goal} onChange={(e) => setTeamForm({ ...teamForm, personal_goal: e.target.value })} />
            <Button type="submit" disabled={addingMember} data-testid="member-add" className="bg-ff-blue text-white hover:bg-ff-blue/90">
              {addingMember ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="mr-2 h-4 w-4" /> Add member</>}
            </Button>
          </form>
          {c.team_members?.length > 0 && (
            <div className="mt-5 space-y-2">
              {c.team_members.map((m, i) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg bg-[#F3F4F6] p-3" data-testid={`manage-member-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-extrabold text-white ${i === 0 ? "bg-ff-red" : "bg-ff-blue"}`}>{i === 0 ? <Trophy className="h-3.5 w-3.5" /> : i + 1}</span>
                    <div>
                      <div className="font-semibold text-ff-blue">{m.name}</div>
                      <div className="text-xs text-ff-medium">{money(m.amount_raised)} raised{m.personal_goal ? ` of ${money(m.personal_goal)}` : ""} · {m.donor_count || 0} donors</div>
                    </div>
                  </div>
                  <button data-testid={`remove-member-${i}`} onClick={() => removeMember(m.id)} className="text-xs font-semibold text-ff-red hover:underline">Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-ff-border bg-white p-4 text-center shadow-sm">
      <div className="font-heading text-xl font-extrabold text-ff-blue">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wider text-ff-medium">{label}</div>
    </div>
  );
}
